import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import axios from "../axios";

function VpisStatistik() {
    const { user } = useAuth();
    const [sheetData, setSheetData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isFullScreen, setIsFullScreen] = useState(false);

    // Get current month and year for display
    const getCurrentMonthYear = () => {
        const date = new Date();
        const month = new Intl.DateTimeFormat("sl-SI", {
            month: "long",
        }).format(date);
        const year = date.getFullYear();
        const formattedMonth = month.charAt(0).toUpperCase() + month.slice(1);
        return {
            display: `${formattedMonth} ${year}`,
            sheet: `${formattedMonth}_${year}`,
        };
    };

    // Fetch the admin's sheet data
    useEffect(() => {
        const fetchSheetData = async () => {
            try {
                const response = await axios.get("/api/admin-sheets/my-sheet", {
                    withCredentials: true,
                });

                if (response.data.success) {
                    setSheetData(response.data);
                } else {
                    setError("No sheet configured");
                }
            } catch (err) {
                setError(
                    err.response?.data?.message ||
                        "Error fetching sheet information"
                );
            } finally {
                setIsLoading(false);
            }
        };

        if (user?.email) {
            fetchSheetData();
        }
    }, []);

    const getSheetUrl = () => {
        if (!sheetData?.sheetId) return "about:blank";

        // If we have a specific sheet gid from the API, use it
        if (sheetData.currentSheet?.gid) {
            return `https://docs.google.com/spreadsheets/d/${sheetData.sheetId}/edit#gid=${sheetData.currentSheet.gid}`;
        }

        // Otherwise, just open the spreadsheet
        return `https://docs.google.com/spreadsheets/d/${sheetData.sheetId}/edit`;
    };

    const toggleFullScreen = () => {
        setIsFullScreen(!isFullScreen);
        // When entering fullscreen, scroll to top
        if (!isFullScreen) {
            window.scrollTo(0, 0);
        }
    };

    const renderFullScreenOverlay = () => {
        if (!isFullScreen) return null;
        return (
            <div
                className="fixed inset-0 z-50 flex flex-col bg-black bg-opacity-80"
                style={{ overflow: "hidden" }}
            >
                <div className="flex items-center justify-between p-4 bg-white bg-opacity-10">
                    <h2 className="font-medium text-white">
                        {sheetData?.name}'s Statistika -{" "}
                        {getCurrentMonthYear().display}
                    </h2>
                    <button
                        className="px-4 py-2 font-medium text-white transition-colors bg-red-600 rounded-lg hover:bg-red-700"
                        onClick={toggleFullScreen}
                    >
                        Izhod iz celozaslonskega načina
                    </button>
                </div>
                <div className="flex items-center justify-center flex-1">
                    <iframe
                        src={getSheetUrl()}
                        className="w-full h-full"
                        frameBorder="0"
                        style={{ background: "white", border: "none" }}
                        title="Statistika"
                        allowFullScreen
                        loading="lazy"
                    />
                </div>
            </div>
        );
    };

    const renderContent = () => {
        if (isLoading) {
            return (
                <div className="flex items-center justify-center h-64">
                    <div className="w-12 h-12 border-b-2 border-blue-600 rounded-full animate-spin"></div>
                </div>
            );
        }

        if (error || !sheetData) {
            return (
                <div className="flex flex-col items-center justify-center h-64">
                    <h2 className="mb-2 text-xl font-semibold text-gray-800">
                        Ni dodeljene tabele
                    </h2>
                    <p className="text-gray-600">
                        Za vas trenutno ni konfigurirana nobena tabela. Prosimo,
                        kontaktirajte administratorja.
                    </p>
                </div>
            );
        }

        return (
            <div className="relative flex-1 overflow-hidden bg-white rounded-lg shadow-md">
                {/* Header with name and fullscreen button */}
                <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between p-4 bg-white border-b">
                    <h2 className="text-lg font-medium text-gray-800">
                        {sheetData.name}'s Statistika -{" "}
                        {getCurrentMonthYear().display}
                    </h2>
                    <button
                        className="px-3 py-1 text-white transition-colors bg-blue-600 rounded hover:bg-blue-700"
                        onClick={toggleFullScreen}
                    >
                        {isFullScreen
                            ? "Izhod iz celozaslonskega načina"
                            : "Celozaslonski prikaz"}
                    </button>
                </div>

                {/* Add padding to account for the header */}
                <div className="h-full pt-16">
                    <iframe
                        src={getSheetUrl()}
                        width="100%"
                        height="100%"
                        frameBorder="0"
                        style={{
                            background: "white",
                            border: "none",
                            minHeight: "700px",
                        }}
                        title="Statistika"
                        allowFullScreen
                        loading="lazy"
                    />
                </div>
            </div>
        );
    };

    if (user.email === "ksenija@stoja-trade.si") {
        return null;
    }

    return (
        <div className="relative flex flex-col w-full h-screen p-4 bg-gray-50">
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-800">
                    Vaša statistika
                </h1>
                <p className="mt-1 text-gray-600">
                    Tukaj lahko urejate in pregledujete vašo statistiko. Vsi
                    podatki se samodejno shranjujejo.
                </p>
            </div>

            {renderContent()}
            {renderFullScreenOverlay()}
        </div>
    );
}

export default VpisStatistik;
